
import { useCallback } from 'react';
import {
  TransitionConfig,
  TransitionEvents,
  UseTransitionReturn,
} from './Transition.model';
import { mergeTransitionConfig } from './transitionConfig';

export type { UseTransitionReturn };

/**
 * Hook to handle the transition animation (fade + slide)
 *
 * @param config - Animation configuration (optional, uses default values)
 * @param events - Events to handle different phases of animation
 * @returns Object with motionProps ready to be passed to motion.div
 *
 * @example
 * ```tsx
 * const { motionProps } = useTransition(
 *   { duration: 0.3, yOffset: 20 },
 *   { onEnterComplete: () => console.log('Animation done') }
 * );
 *
 * <motion.div {...motionProps}>Content</motion.div>
 * ```
 */
export const useTransition = (
  config?: Partial<TransitionConfig>,
  events?: TransitionEvents
): UseTransitionReturn => {
  const finalConfig = mergeTransitionConfig(config);

  const handleAnimationStart = useCallback(
    (definition: any) => {
      if (definition === 'animate' || definition?.opacity === 1) {
        events?.onEnterStart?.();
      } else {
        events?.onExitStart?.();
      }
    },
    [events]
  );

  const handleAnimationComplete = useCallback(
    (definition: any) => {
      if (definition === 'animate' || definition?.opacity === 1) {
        events?.onEnterComplete?.();
      } else {
        events?.onExitComplete?.();
      }
    },
    [events]
  );

  const getYOffset = (isExit: boolean = false) => {
    if (!finalConfig.enabled) return 0;

    if (finalConfig.direction === 'forward') {
      return isExit ? -finalConfig.yOffset : finalConfig.yOffset;
    } else if (finalConfig.direction === 'backward') {
      return isExit ? finalConfig.yOffset : -finalConfig.yOffset;
    }
    return isExit ? -finalConfig.yOffset : finalConfig.yOffset;
  };

  const motionProps: UseTransitionReturn['motionProps'] = {
    initial: {
      y: getYOffset(false),
      opacity: finalConfig.enabled ? 0 : 1,
    },
    animate: {
      y: 0,
      opacity: 1,
      willChange: 'transform',
    },
    exit: {
      y: getYOffset(true),
      opacity: finalConfig.enabled ? 0 : 1,
    },
    transition: {
      duration: finalConfig.duration,
    },
  };

  const { onEnterStart, onEnterComplete, onExitStart, onExitComplete } = events || {};
  if (onEnterStart || onEnterComplete || onExitStart || onExitComplete) {
    motionProps.onAnimationStart = handleAnimationStart;
    motionProps.onAnimationComplete = handleAnimationComplete;
  }

  return {
    motionProps,
    config: finalConfig,
  };
};

